import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { AllieText, AllieAvatar, AllieTextArea, AllieButton } from '../../../components';
import { StyledCommentsContainer } from './Comments.styles';
import { AllieCommentsProps } from './Comments.types';
import { isMobile } from '../../../common/utils';

dayjs.extend(relativeTime);

const getDatetime = (createdAt: string) => {
  const date = dayjs(createdAt);
  const now = dayjs();
  const diff = now.diff(date, 'hour');
  if (diff >= 24) {
    return date.format('DD-MM-YYYY');
  }
  return date.fromNow();
};

export const AllieComments: React.FC<AllieCommentsProps> = ({
  comments,
  avatar,
  name,
  onSubmit,
  labelSaveButton,
  maxCommentsVisible = 10,
  placeholder,
}) => {
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  const namespace = 'allie-comments';

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (inputContainerRef.current && !inputContainerRef.current.contains(event.target)) {
        setShowSaveButton(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [inputContainerRef]);

  const handleSave = async (event: any) => {
    setIsLoading(true);
    if (event) {
      event.preventDefault();
    }
    await onSubmit(text);
    setIsLoading(false);
    setText('');
    setShowSaveButton(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSave(event);
    }
  };

  return (
    <StyledCommentsContainer maxCommentsVisible={maxCommentsVisible} className={namespace}>
      <div className={`${namespace}__new`}>
        <AllieAvatar height={40} className={`${namespace}__avatar`} src={avatar} name={name}>
          <img src={avatar} />
        </AllieAvatar>
        <div className={`${namespace}__container-input`} ref={inputContainerRef}>
          <AllieTextArea
            className={`${namespace}__container-input__comment`}
            corner="rounded-edges"
            onChange={(e: any) => {
              setText(e.target.value);
            }}
            placeholder={placeholder}
            onFocus={() => setShowSaveButton(true)}
            onKeyPress={handleKeyPress}
            value={text}
            allieSize="small"
          />
          {showSaveButton && (
            <div className={`${namespace}__container-input__button`}>
              <AllieButton
                allieType="primary"
                onclick={handleSave}
                size="small"
                loading={isLoading}
              >
                {labelSaveButton}
              </AllieButton>
            </div>
          )}
        </div>
      </div>
      {comments && comments.length > 0 && (
        <div className={`${namespace}__list`}>
          {comments.map(({ commentedBy, comment, createdAt }) => (
            <div key={`${commentedBy.name}-${createdAt}`} className={`${namespace}__list-item`}>
              <AllieAvatar
                height={40}
                className={`${namespace}__avatar`}
                src={commentedBy.avatar}
                name={commentedBy.name}
              >
                <img src={commentedBy.avatar} />
              </AllieAvatar>
              <div className={`${namespace}__comment-content`}>
                <div className={`${namespace}__title`}>
                  <AllieText type="body2" color="neutral" className={`${namespace}__name`}>
                    {commentedBy.name}
                  </AllieText>
                  <AllieText type="body2" color="neutral-60">
                    {getDatetime(createdAt)}
                  </AllieText>
                </div>
                <AllieText type="body2" color="neutral">
                  {comment}
                </AllieText>
              </div>
            </div>
          ))}
        </div>
      )}
    </StyledCommentsContainer>
  );
};
